import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  AddInvitationReminder,
  GetInvitationChecklist,
  GetInvitationRemindersByEventId,
  SetInvitationCallStatus,
  UpdateInvitationReminder,
} from '../../api/api';
import { fmtDate } from '../../utils/helpers';

const today = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const timeNow = () => new Date().toTimeString().slice(0, 5);
export const toTimeInput = (v) => (v ? String(v).slice(0, 5) : '');
const addMinutes = (time, minutes) => {
  const [h, m] = toTimeInput(time).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};
// Each guest gets a suggested call slot: reminder time + 5 min × position.
const withReminderSlots = (list, reminder) =>
  list.map((row, index) => ({ ...row, ReminderSlot: addMinutes(reminder?.ReminderTime, index * 5) }));
export const dueText = (r, callDate) => {
  if (!r) return '';
  if (!r.Active) return 'Paused';
  if (String(callDate) < String(r.StartDate)) return `Starts ${fmtDate(r.StartDate)}`;
  if (String(callDate) === String(r.StartDate) && timeNow() < toTimeInput(r.ReminderTime)) {
    return `Today at ${toTimeInput(r.ReminderTime)}`;
  }
  return `Due daily at ${toTimeInput(r.ReminderTime)}`;
};
const blankForm = () => ({ Title: 'Invitation Calls', StartDate: today(), ReminderTime: '14:00', Active: 1, Notes: '' });

// Invitation-call reminders: daily reminder(s) per event + a per-day checklist
// of guests to call. All logic here; Reminders.desktop / .mobile only render.
export function useReminders(event) {
  const { user, organiser, isAdmin, can } = useAuth();
  const toast = useToast();
  const canUse = isAdmin || can('CanReminders') || can('CanMembers');
  const canManage = isAdmin || can('CanRemindersAdd') || can('CanReminders') || can('CanMembersAdd') || can('CanMembers');
  const canDelete = isAdmin || can('CanRemindersDelete') || can('CanReminders') || can('CanMembersDelete') || can('CanMembers');

  const [reminders, setReminders] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [callDate, setCallDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);

  const selected = reminders.find(r => String(r.ReminderId) === String(selectedId)) || reminders[0] || null;
  const activeCount = useMemo(() => reminders.filter(r => Number(r.Active) === 1).length, [reminders]);
  const called = rows.filter(r => Number(r.Called) === 1).length;

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GetInvitationRemindersByEventId({ EventId: event.EventId });
      const list = Array.isArray(data) ? data : [];
      setReminders(list);
      setSelectedId(prev => (prev && list.some(r => String(r.ReminderId) === String(prev))) ? prev : (list[0]?.ReminderId || ''));
    } catch {
      toast('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, [event, toast]);

  const loadChecklist = useCallback(async () => {
    if (!selected) { setRows([]); return; }
    setListLoading(true);
    try {
      const data = await GetInvitationChecklist({
        EventId: event.EventId,
        ReminderId: selected.ReminderId,
        CallDate: callDate,
        UserId: user.UserId,
        IsAdmin: isAdmin ? 1 : 0,
      });
      setRows(withReminderSlots(Array.isArray(data) ? data : [], selected));
    } catch {
      toast('Failed to load calling list');
    } finally {
      setListLoading(false);
    }
  }, [event, selected, callDate, user, isAdmin, toast]);

  useEffect(() => { loadReminders(); }, [loadReminders]);
  useEffect(() => { loadChecklist(); }, [loadChecklist]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openAdd = () => { setEditing(null); setForm(blankForm()); setModalOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      Title: r.Title || 'Invitation Calls',
      StartDate: String(r.StartDate || today()).slice(0, 10),
      ReminderTime: toTimeInput(r.ReminderTime) || '14:00',
      Active: Number(r.Active) === 1 ? 1 : 0,
      Notes: r.Notes || '',
    });
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  const saveReminder = async () => {
    if (!form.StartDate) return toast('From date is required');
    if (!form.ReminderTime) return toast('Reminder time is required');
    const payload = {
      ...form,
      ReminderTime: `${form.ReminderTime}:00`,
      EventId: event.EventId,
      OrganiserId: organiser.OrganiserId,
      UpdatedBy: user.UserId,
    };
    try {
      if (editing) {
        await UpdateInvitationReminder({ data: payload, where: `ReminderId = ${editing.ReminderId}` });
        toast('Reminder updated');
      } else {
        await AddInvitationReminder({ ...payload, Valid: 1, CreatedBy: user.UserId });
        toast('Reminder added');
      }
      setModalOpen(false);
      loadReminders();
    } catch {
      toast('Save failed');
    }
  };

  const deleteReminder = async (r) => {
    if (!r?.ReminderId) return;
    if (!window.confirm(`Delete reminder "${r.Title || 'Invitation Calls'}"?`)) return;
    try {
      await UpdateInvitationReminder({ data: { Valid: 0, UpdatedBy: user.UserId }, where: `ReminderId = ${r.ReminderId}` });
      toast('Reminder deleted');
      if (String(selectedId) === String(r.ReminderId)) setSelectedId('');
      await loadReminders();
    } catch {
      toast('Delete failed');
    }
  };

  // Optimistic tick; rolled back if the API call fails.
  const toggleCalled = async (row) => {
    const next = Number(row.Called) === 1 ? 0 : 1;
    setRows(list => list.map(r => r.GuestId === row.GuestId ? { ...r, Called: next } : r));
    try {
      await SetInvitationCallStatus({
        ReminderId: selected.ReminderId,
        EventId: event.EventId,
        GuestId: row.GuestId,
        GuestIds: row.GuestIds,
        CallDate: callDate,
        Called: next,
        UserId: user.UserId,
      });
      if (next) toast('Marked as called');
    } catch {
      setRows(list => list.map(r => r.GuestId === row.GuestId ? { ...r, Called: row.Called } : r));
      toast('Could not update call status');
    }
  };

  const isSelected = (r) => String(selected?.ReminderId) === String(r.ReminderId);

  return {
    canUse, canManage, canDelete,
    reminders, selected, selectedId, setSelectedId, isSelected, activeCount,
    callDate, setCallDate, rows, called, loading, listLoading,
    modalOpen, closeModal, editing, form, setF, openAdd, openEdit, saveReminder, deleteReminder, toggleCalled,
  };
}
